import { useCallback, useMemo, useState } from 'react';
import { Avatar, Breadcrumb, Card, Empty, List, Typography } from 'antd';
import { useEmployeesMock } from '@/entities/employee/api/hooks';
import type { OrgTreeNode } from '@/entities/organization/model/organizationSchema';
import { useOrgTree } from '@/entities/organization/api/hooks';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import type { Employee } from '@/entities/employee/model/employeeSchema';
import { departmentsUnderNode } from '../utils';
import s from './OrgTreePanel.module.scss';

const { Text } = Typography;

function buildPath(nodeId: string, nodeMap: Map<string, OrgTreeNode>, tree: OrgTreeNode[]): OrgTreeNode[] {
  const path: OrgTreeNode[] = [];
  let current = nodeMap.get(nodeId);
  while (current) {
    path.unshift(current);
    const parent = tree.find((n) => n.children.includes(current!.id));
    current = parent ? nodeMap.get(parent.id) : undefined;
  }
  return path;
}

function LegacyEmployeeRow({ emp, onClick }: { emp: Employee; onClick: () => void }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
    >
      <Avatar size={32}>{emp.initials}</Avatar>
      <div>
        <Text strong>{emp.name}</Text>
        <div style={{ fontSize: 12, color: '#888' }}>
          {emp.position} · {emp.department}
        </div>
      </div>
    </div>
  );
}

export function OrgTreePanel({ onSelectEmployee }: { onSelectEmployee: (id: number) => void }) {
  const { data: tree, isLoading: treeLoading } = useOrgTree();
  const { data: allEmps, isLoading: empsLoading } = useEmployeesMock();

  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);

  const nodeMap = useMemo(() => new Map(tree?.map((n) => [n.id, n]) ?? []), [tree]);

  const rootNodes = useMemo(() => tree?.filter((n) => n.depth === 0) ?? [], [tree]);

  const currentNode = currentNodeId ? nodeMap.get(currentNodeId) : undefined;

  const childNodes = useMemo(() => {
    if (!currentNode) return rootNodes;
    return currentNode.children.map((cid) => nodeMap.get(cid)).filter(Boolean) as OrgTreeNode[];
  }, [currentNode, rootNodes, nodeMap]);

  const breadcrumbPath = useMemo(() => {
    if (!currentNodeId || !tree?.length) return [];
    return buildPath(currentNodeId, nodeMap, tree);
  }, [currentNodeId, nodeMap, tree]);

  const deptEmps = useMemo(() => {
    if (!currentNodeId || !allEmps?.length || !nodeMap.size) return [];
    const depts = departmentsUnderNode(currentNodeId, nodeMap);
    return allEmps.filter((e) => depts.has(e.department));
  }, [currentNodeId, allEmps, nodeMap]);

  const empCountForNode = useCallback(
    (nodeId: string) => {
      if (!allEmps?.length || !nodeMap.size) return 0;
      const depts = departmentsUnderNode(nodeId, nodeMap);
      return allEmps.filter((e) => depts.has(e.department)).length;
    },
    [allEmps, nodeMap],
  );

  if (treeLoading || empsLoading) return <PageSpinner />;

  return (
    <div className={s.root}>
      <Card size="small" className={s.root__nav}>
        <div className={s.root__header}>
          <Breadcrumb
            items={[
              {
                title: (
                  <a onClick={() => setCurrentNodeId(null)} className={s.root__crumb}>
                    Все подразделения
                  </a>
                ),
              },
              ...breadcrumbPath.map((node) => ({
                title: (
                  <a onClick={() => setCurrentNodeId(node.id)} className={s.root__crumb}>
                    {node.label}
                  </a>
                ),
              })),
            ]}
          />
        </div>

        {childNodes.length > 0 && (
          <List
            size="small"
            className={s.root__list}
            dataSource={childNodes}
            renderItem={(node) => {
              const count = empCountForNode(node.id);
              return (
                <List.Item className={s['root__list-item']} onClick={() => setCurrentNodeId(node.id)}>
                  <div className={s['root__list-item-content']}>
                    <span className={s['root__list-label']}>{node.label}</span>
                    <Text type="secondary" className={s['root__list-count']}>
                      {count} чел.
                    </Text>
                  </div>
                </List.Item>
              );
            }}
          />
        )}

        {childNodes.length === 0 && !currentNode && <Empty description="Оргструктура пуста" />}
      </Card>

      {currentNode && (
        <Card size="small" className={s.root__employees} title={`Сотрудники — ${currentNode.label}`}>
          <Text className={s['root__emp-count']}>
            Всего: <strong>{deptEmps.length}</strong>
          </Text>
          {deptEmps.length > 0 ? (
            deptEmps.map((emp) => <LegacyEmployeeRow key={emp.id} emp={emp} onClick={() => onSelectEmployee(emp.id)} />)
          ) : (
            <Empty description="Нет сотрудников в этом подразделении" />
          )}
        </Card>
      )}
    </div>
  );
}
